import { Box, Button, Card, Container, Group, Stack, Text, Title, Badge, Tabs } from '@mantine/core';
import { formatDateTime } from '@medplum/core';
import { Appointment, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconCalendar, IconVideo, IconFileDescription } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import type { JSX } from "react";

export function AppointmentsPage(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const patient = medplum.getProfile() as Patient;

  // Mock appointments data - replace with actual API call
  const upcomingAppointments = [
    {
      id: '1',
      start: '2024-03-14T14:00:00Z',
      end: '2024-03-14T15:00:00Z',
      status: 'booked',
      practitioner: 'Dr. Sarah Johnson',
      type: 'Individual Therapy',
      location: 'Video Session'
    },
    {
      id: '2',
      start: '2024-03-21T14:00:00Z',
      end: '2024-03-21T15:00:00Z',
      status: 'booked',
      practitioner: 'Dr. Sarah Johnson',
      type: 'Individual Therapy',
      location: 'Video Session'
    }
  ];

  const requestedAppointments = [
    {
      id: '3',
      start: '2024-03-28T15:00:00Z',
      end: '2024-03-28T16:00:00Z',
      status: 'pending',
      practitioner: 'Dr. Sarah Johnson',
      type: 'Individual Therapy',
      location: 'Video Session'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'booked':
        return 'blue';
      case 'arrived':
        return 'green';
      case 'cancelled':
        return 'red';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const AppointmentCard = ({ appointment }: { appointment: any }) => (
    <Card key={appointment.id} withBorder shadow="sm">
      <Group justify="apart" mb="md">
        <Group>
          <IconCalendar size={24} />
          <div>
            <Text fw={500}>{appointment.type}</Text>
            <Text size="sm" c="dimmed">
              {formatDateTime(appointment.start)}
            </Text>
          </div>
        </Group>
        <Badge color={getStatusColor(appointment.status)}>
          {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
        </Badge>
      </Group>

      <Group justify="apart">
        <div>
          <Text size="sm" fw={500}>Provider</Text>
          <Text size="sm">{appointment.practitioner}</Text>
        </div>
        <div>
          <Text size="sm" fw={500}>Location</Text>
          <Text size="sm">{appointment.location}</Text>
        </div>
      </Group>

      <Group mt="xl">
        {appointment.status === 'booked' && (
          <>
            <Button 
              variant="light" 
              leftSection={<IconVideo size={16} />}
              onClick={() => navigate('/get-care')}
            >
              Join Session
            </Button>
            <Button 
              variant="light" 
              leftSection={<IconFileDescription size={16} />}
              onClick={() => navigate('/documents/forms')}
            >
              View Forms
            </Button>
          </>
        )}
        {appointment.status === 'pending' && (
          <Button 
            variant="light" 
            color="red"
            onClick={() => {
              // Handle cancellation
              console.log('Cancel request');
            }}
          >
            Cancel Request
          </Button>
        )}
      </Group>
    </Card>
  );

  return (
    <Container size="lg" py="xl">
      <Group justify="apart" mb="xl">
        <Title>My Appointments</Title>
        <Button onClick={() => navigate('/get-care')}>Schedule New Session</Button>
      </Group>

      <Tabs defaultValue="upcoming">
        <Tabs.List mb="md">
          <Tabs.Tab value="upcoming">
            Upcoming Appointments
          </Tabs.Tab>
          <Tabs.Tab value="requested">
            Requested Appointments
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="upcoming">
          <Stack gap="md">
            {upcomingAppointments.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">No upcoming appointments</Text>
            ) : (
              upcomingAppointments.map((appointment) => (
                <AppointmentCard key={appointment.id} appointment={appointment} />
              ))
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="requested">
          <Stack gap="md">
            {requestedAppointments.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">No requested appointments</Text>
            ) : (
              requestedAppointments.map((appointment) => (
                <AppointmentCard key={appointment.id} appointment={appointment} />
              ))
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}